import { zodResolver } from "@hookform/resolvers/zod"
import { Box, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"

import { PriceChangeTableVariant, PricingNumericTypeValues } from "../../constants"
import NumericTypeField from "../forms/NumericTypeField"

import { Button } from "@/components/common/Button"
import { FormCheckbox, FormNumericField } from "@/components/UiKit"
import { CURRENCY_RENDER_PRECISION } from "@/constants/formats"
import { type PriceChangeItemGroup, PriceChangeType } from "@/graphql/codegen/graphql"
import {
  type WarehouseHeaderFormValues,
  warehouseHeaderFormSchema,
} from "@/screens/Pricing/schema/WarehouseHeaderSchema"
import { pricingCalc } from "@/screens/Pricing/utils/pricingCalc"
import { formatNumber, roundToPrecision } from "@/utils/number"
import { formatLocationAddress } from "@/utils/utils"

interface PricingWarehouseHeaderProps {
  group: PriceChangeItemGroup
  priceChangeType: PriceChangeType
  onPriceChangeApply: (values: WarehouseHeaderFormValues) => void
  onCostChangeApply: (values: WarehouseHeaderFormValues) => void
  onAcceptedCostChange?: (value: number | null) => void
  readonly?: boolean
  variant: PriceChangeTableVariant
  hasSelectedRows: boolean
}

export const PricingWarehouseHeader: FC<PricingWarehouseHeaderProps> = ({
  group,
  onPriceChangeApply,
  onCostChangeApply,
  onAcceptedCostChange,
  readonly,
  priceChangeType,
  variant,
  hasSelectedRows,
}) => {
  const {
    shipFromWarehouseArea,
    shipFromLocation,
    incoterms,
    incotermsLocation,
    lastBareCost,
    lastLaidDownCost,
    previousAcceptedCost,
    acceptedCost,
  } = group

  const shipFromLocationField = {
    [PriceChangeType.SellingPrice]: shipFromWarehouseArea?.location,
    [PriceChangeType.PurchasingCost]: shipFromLocation,
  }[priceChangeType]

  const isSubmitPriceChangeVariant = variant === PriceChangeTableVariant.SubmitPriceChange

  const methods = useForm<WarehouseHeaderFormValues>({
    resolver: zodResolver(warehouseHeaderFormSchema),
    defaultValues: {
      desiredAcceptedCostChange: null,
      acceptedCostChangeMode: PricingNumericTypeValues.Percent,
      newAcceptedCost: (isSubmitPriceChangeVariant ? previousAcceptedCost : acceptedCost) ?? null,
      desiredPriceChange: null,
      priceChangeMode: PricingNumericTypeValues.Percent,
      maintainMargin: false,
    },
  })

  const { control, handleSubmit, setValue } = methods

  const incotermsLocationLabel = formatLocationAddress(incotermsLocation ?? null) ?? ""

  const formatCurrency = (value?: number | null) =>
    `$${formatNumber(value ?? 0, CURRENCY_RENDER_PRECISION, CURRENCY_RENDER_PRECISION)}`

  const acceptedCostChangeMode = useWatch({ control, name: "acceptedCostChangeMode" })
  const desiredAcceptedCostChange = useWatch({ control, name: "desiredAcceptedCostChange" })
  const newAcceptedCost = useWatch({ control, name: "newAcceptedCost" })

  const isApplyPriceChangeDisabled = readonly || !hasSelectedRows

  const hasCostChange = (newAcceptedCost ?? null) !== null && newAcceptedCost !== (previousAcceptedCost ?? null)
  const isApplyCostChangeDisabled = readonly || !hasCostChange

  // Empty change → fall back to the current accepted cost (same as a 0 change).
  const deriveNewAcceptedCost = (changeValue: number | null, changeType: PricingNumericTypeValues): number | null =>
    roundToPrecision(
      changeValue === null
        ? (previousAcceptedCost ?? 0)
        : (pricingCalc.newAcceptedCost({
            currentAcceptedCost: previousAcceptedCost ?? 0,
            changeValue,
            changeType,
          }) ?? 0),
      CURRENCY_RENDER_PRECISION
    )

  const handleDesiredAcceptedCostChange = (value: number | null) => {
    const nextAcceptedCost = deriveNewAcceptedCost(value, acceptedCostChangeMode)
    setValue("newAcceptedCost", nextAcceptedCost)
    onAcceptedCostChange?.(nextAcceptedCost)
  }

  const handleNewAcceptedCostChange = (value: number | null) => {
    setValue(
      "desiredAcceptedCostChange",
      roundToPrecision(
        pricingCalc.acceptedCostChange({
          currentAcceptedCost: previousAcceptedCost ?? 0,
          newAcceptedCost: value,
          changeType: acceptedCostChangeMode,
        }) ?? 0,
        CURRENCY_RENDER_PRECISION
      )
    )
    onAcceptedCostChange?.(value)
  }

  const handleAcceptedCostChangeModeChange = (mode: PricingNumericTypeValues) => {
    const nextAcceptedCost = deriveNewAcceptedCost(desiredAcceptedCostChange, mode)
    setValue("newAcceptedCost", nextAcceptedCost)
    onAcceptedCostChange?.(nextAcceptedCost)
  }

  const handlePriceChangeApply = (values: WarehouseHeaderFormValues) => {
    onPriceChangeApply(values)
  }

  const handleCostChangeApply = (values: WarehouseHeaderFormValues) => {
    onCostChangeApply(values)
  }

  return (
    <FormProvider {...methods}>
      <Box
        component='form'
        onSubmit={handleSubmit(handlePriceChangeApply)}
        className='flex items-start gap-3 border border-y-0 border-solid border-gray-400 bg-primary-100 px-6 py-4'
      >
        <Box className='flex gap-6'>
          <Box className='flex flex-col gap-1 py-2'>
            <Typography variant='body2' className='text-gray-700'>
              Ship From:
            </Typography>
            <Typography variant='body2' className='font-medium text-gray-800'>
              {formatLocationAddress(shipFromLocationField ?? null)}
            </Typography>
          </Box>

          <Box className='flex flex-col gap-1 py-2'>
            <Typography variant='body2' className='text-gray-700'>
              Incoterms:
            </Typography>
            <Typography variant='body2' className='font-medium text-gray-800'>
              {incoterms}
            </Typography>
          </Box>

          <Box className='flex flex-col gap-1 py-2'>
            <Typography variant='body2' className='text-gray-700'>
              Incoterms Location:
            </Typography>
            <Typography variant='body2' className='font-medium text-gray-800'>
              {incotermsLocationLabel}
            </Typography>
          </Box>
        </Box>

        {priceChangeType === PriceChangeType.SellingPrice && (
          <>
            <TextField
              label='Last Bare Cost'
              value={formatCurrency(lastBareCost)}
              disabled
              size='small'
              className='w-[144px]'
            />

            <TextField
              label='Last Laid Down Cost'
              value={formatCurrency(lastLaidDownCost)}
              disabled
              size='small'
              className='w-[168px]'
            />

            <TextField
              label='Current Accepted Cost'
              value={formatCurrency(previousAcceptedCost)}
              disabled
              size='small'
              className='w-[144px]'
            />
          </>
        )}

        {priceChangeType === PriceChangeType.SellingPrice && isSubmitPriceChangeVariant && (
          <Box className='flex flex-col gap-1'>
            <Typography variant='caption' className='leading-normal text-gray-800'>
              Desired Accepted Cost Change
            </Typography>
            <Box className='flex items-end gap-3'>
              <FormNumericField
                control={control}
                name='desiredAcceptedCostChange'
                size='small'
                className='w-24'
                disabled={readonly}
                decimalScale={CURRENCY_RENDER_PRECISION}
                onChange={handleDesiredAcceptedCostChange}
                data-testid='pricing.warehouse-header.desired-accepted-cost-change-field'
              />

              <NumericTypeField
                name='acceptedCostChangeMode'
                disabled={readonly}
                onChange={handleAcceptedCostChangeModeChange}
              />
            </Box>

            <FormCheckbox
              control={control}
              name='maintainMargin'
              disabled={readonly}
              size='small'
              label={
                <Typography variant='caption' className='text-gray-800'>
                  Maintain margins
                </Typography>
              }
              data-testid='pricing.warehouse-header.maintain-margins-checkbox'
            />
          </Box>
        )}

        {priceChangeType === PriceChangeType.SellingPrice && (
          <Box className='flex flex-col gap-1'>
            <Typography variant='caption' className='leading-normal text-gray-800'>
              New Accepted Cost
            </Typography>
            <FormNumericField
              control={control}
              name='newAcceptedCost'
              prefix='$'
              size='small'
              className='w-[144px]'
              disabled={!isSubmitPriceChangeVariant || readonly}
              decimalScale={CURRENCY_RENDER_PRECISION}
              fixedDecimalScale={true}
              allowNegative={false}
              onChange={handleNewAcceptedCostChange}
              data-testid='pricing.warehouse-header.new-accepted-cost-field'
            />
          </Box>
        )}

        {priceChangeType === PriceChangeType.SellingPrice && isSubmitPriceChangeVariant && (
          <Box className='pt-5'>
            <Button
              type='button'
              appearance='outlined'
              disabled={isApplyCostChangeDisabled}
              onClick={handleSubmit(handleCostChangeApply)}
              data-testid='pricing.warehouse-header.apply-cost-change-button'
            >
              Apply Cost Change
            </Button>
          </Box>
        )}

        {[PriceChangeTableVariant.SubmitPriceChange, PriceChangeTableVariant.ReviewSubmission].includes(variant) && (
          <Box className='flex flex-col gap-1'>
            <Typography variant='caption' className='leading-normal text-gray-800'>
              Desired Price Change
            </Typography>
            <Box className='flex items-end gap-3'>
              <FormNumericField
                control={control}
                name='desiredPriceChange'
                size='small'
                className='w-24'
                disabled={readonly}
                decimalScale={CURRENCY_RENDER_PRECISION}
                data-testid='pricing.warehouse-header.desired-price-change-field'
              />

              <NumericTypeField name='priceChangeMode' disabled={readonly} />
            </Box>
          </Box>
        )}

        {[PriceChangeTableVariant.SubmitPriceChange, PriceChangeTableVariant.ReviewSubmission].includes(variant) && (
          <Box className='pt-5'>
            <Button
              type='submit'
              appearance='outlined'
              disabled={isApplyPriceChangeDisabled}
              data-testid='pricing.warehouse-header.apply-price-change-button'
            >
              Apply Price Change
            </Button>
          </Box>
        )}
      </Box>
    </FormProvider>
  )
}
